export interface ExternalClientPermission {
    ClientID: number | string;
    ClientName?: string;
    Permissions?: string[];
}

const normalisePermissions = (permissions: unknown): string[] =>
    Array.isArray(permissions) ? permissions.filter((permission): permission is string => typeof permission === 'string') : [];

export const getExternalClientPermissions = (value: unknown): ExternalClientPermission[] => {
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!Array.isArray(parsed)) return [];

        return parsed
            .filter((client): client is ExternalClientPermission => client && client.ClientID !== undefined)
            .map((client) => ({ ...client, Permissions: normalisePermissions(client.Permissions) }));
    } catch {
        return [];
    }
};

export const hasExternalClientPermission = (
    clientPermissions: ExternalClientPermission[],
    requiredPermissions: string | string[]
) => {
    const required = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
    return clientPermissions.some((client) =>
        required.some((permission) => client.Permissions?.includes(permission))
    );
};

export const clientIdsWithExternalPermission = (
    clientPermissions: ExternalClientPermission[],
    requiredPermissions: string | string[]
) => {
    const required = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
    return new Set(
        clientPermissions
            .filter((client) => required.some((permission) => client.Permissions?.includes(permission)))
            .map((client) => String(client.ClientID))
    );
};

// Checks whether a single client (by id) has one of the required permissions -
// used to gate page content once the dropdown lists every client regardless of permission.
export const clientHasExternalPermission = (
    clientPermissions: ExternalClientPermission[],
    clientId: unknown,
    requiredPermissions: string | string[]
) => {
    if (clientId === undefined || clientId === null || clientId === '') return false;

    const required = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
    const client = clientPermissions.find((item) => String(item.ClientID) === String(clientId));

    return !!client && required.some((permission) => client.Permissions?.includes(permission));
};